import { useState, useRef, useCallback } from 'react';
import { api } from '../../api';
import type {
  BatchUploadFileSpec,
  BatchUploadUrlResult,
  CompleteUploadBatchResult,
} from '../../api';
import { getErrorMessage, busyTracker } from '../../../../shared/src';
import {
  computeFileHash,
  getFileValidationError,
} from '../../utils/fileHash';
import {
  CollectedFile,
  collectFilesFromEntry,
  extractFolderPaths,
} from '../../utils/folderTraversal';
import { mapWithConcurrency, chunk } from '../../utils/concurrency';
import { sanitizeFileNameForCollision } from '../../utils/uploadName';

export interface UploadResult {
  fileName: string;
  status: 'uploaded' | 'skipped' | 'renamed' | 'failed' | 'folder_created' | 'folder_skipped';
  message?: string;
  newName?: string;
  // For failed results only: whether retrying could succeed. Transient failures
  // (network, storage, server) are retryable; validation/filename rejections are not.
  // Undefined is treated as retryable, since most upload failures are transient — the
  // recap shows a "try again" hint unless this is explicitly false.
  retryable?: boolean;
}

export interface PendingFile {
  file: File;
  id: string;
  validationError: string | null;
  relativePath?: string;
}

interface UseUploadOptions {
  // The folder ID writes should target. At the project root this must be
  // project.root_folder_id (the __root__ sentinel), not null, so the row stays
  // visible to the listing query.
  targetFolderId: string | null;
  projectId: string;
  onUploadComplete: () => void;
}

// One file to upload, with the destination folder and the label shown in the recap.
interface UploadItem {
  file: File;
  folderId?: string;
  displayName: string;
}

// Tunables for bulk uploads. Per-file API round-trips are collapsed into batches
// (the WAF limits requests/IP, but presigned S3 PUTs bypass it — so parallelism lives
// in the PUTs). See the plan + lambda/app-api/upload-handlers.ts.
const BATCH_SIZE = 200;          // files per get-urls / complete-batch request (<= backend cap of 256)
const BATCH_CONCURRENCY = 6;     // independent batches in flight when every target is unique
const FOLDER_CONCURRENCY = 4;    // folders in flight when duplicate names force per-folder ordering
const HASH_CONCURRENCY = 6;      // files hashed at once (bounds memory)
const PUT_CONCURRENCY = 10;      // concurrent S3 PUTs per batch

async function uploadToS3(presignedUrl: string, file: File) {
  let response: Response;
  try {
    response = await fetch(presignedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
      },
    });
  } catch {
    // Network-level failure (offline, dropped connection, CORS). Surface a clean,
    // actionable message instead of the browser's raw "Failed to fetch (<bucket-host>)",
    // which is cryptic and needlessly exposes the internal bucket hostname.
    throw new Error('Network error while uploading the file');
  }
  if (!response.ok) {
    throw new Error(`Storage rejected the upload (HTTP ${response.status})`);
  }
}

// Hash + presign + PUT + confirm one batch of files, pushing a UploadResult per file.
async function runBatch(
  batch: UploadItem[],
  projectId: string,
  push: (r: UploadResult) => void
) {
  // 1. Validate + hash (bounded concurrency so we never hold many files in memory).
  type Hashed = { item: UploadItem; spec?: BatchUploadFileSpec; error?: string; retryable?: boolean };
  const hashed: Hashed[] = await mapWithConcurrency(batch, HASH_CONCURRENCY, async (item): Promise<Hashed> => {
    const validationError = getFileValidationError(item.file);
    // Validation failures (wrong type, too large) can't pass on a retry — flag them so
    // the recap doesn't tell the user to try again. (The hash error below is transient.)
    if (validationError) return { item, error: validationError, retryable: false };
    try {
      const fileHash = await computeFileHash(item.file);
      return {
        item,
        spec: {
          fileName: item.file.name,
          fileSize: item.file.size,
          contentType: item.file.type || 'application/octet-stream',
          fileHash,
          folderId: item.folderId,
        },
      };
    } catch (err) {
      return { item, error: getErrorMessage(err) };
    }
  });

  for (const h of hashed) {
    if (h.error) push({ fileName: h.item.displayName, status: 'failed', message: h.error, retryable: h.retryable });
  }
  const okHashed = hashed.filter(
    (h): h is { item: UploadItem; spec: BatchUploadFileSpec } => !!h.spec
  );
  if (okHashed.length === 0) return;

  // 2. One presign request for the whole batch.
  let urlResults: BatchUploadUrlResult[];
  try {
    urlResults = await api.getUploadUrls(projectId, okHashed.map((h) => h.spec));
  } catch (err) {
    const msg = getErrorMessage(err);
    for (const h of okHashed) push({ fileName: h.item.displayName, status: 'failed', message: msg });
    return;
  }

  // 3. Resolve skips/errors; queue the rest for S3 upload.
  interface PutTarget {
    uploadId: string;
    uploadUrl: string;
    item: UploadItem;
    renamed: boolean;
    fileName: string;
  }
  const putTargets: PutTarget[] = [];
  for (const r of urlResults) {
    const item = okHashed[r.index]?.item;
    if (!item) continue;
    if (r.error) {
      // The backend flags per-file presign errors: validation/filename rejections (wrong
      // type, too large, invalid name) aren't retryable, but a thrown DB/S3 failure is.
      // Undefined (older backend) falls back to retryable, so we never wrongly tell the
      // user a transient failure is permanent.
      push({ fileName: item.displayName, status: 'failed', message: r.error, retryable: r.retryable });
    } else if (r.skipped) {
      const folderInfo = r.existingFolderPath ? ` in folder /${r.existingFolderPath}` : ' at root level';
      push({
        fileName: item.displayName,
        status: 'skipped',
        message: `Already exists as "${r.existingFileName}"${folderInfo}`,
      });
    } else if (r.uploadId && r.uploadUrl) {
      putTargets.push({
        uploadId: r.uploadId,
        uploadUrl: r.uploadUrl,
        item,
        renamed: !!r.renamed,
        fileName: r.fileName || item.file.name,
      });
    } else {
      push({ fileName: item.displayName, status: 'failed', message: 'No upload URL returned' });
    }
  }
  if (putTargets.length === 0) return;

  // 4. Upload to S3 (bounded concurrency; PUTs bypass the WAF).
  const putOk: PutTarget[] = [];
  const putOutcomes = await mapWithConcurrency(putTargets, PUT_CONCURRENCY, async (t) => {
    try {
      await uploadToS3(t.uploadUrl, t.item.file);
      return { t, ok: true as const };
    } catch (err) {
      return { t, ok: false as const, error: getErrorMessage(err) };
    }
  });
  for (const o of putOutcomes) {
    if (o.ok) putOk.push(o.t);
    else push({ fileName: o.t.item.displayName, status: 'failed', message: o.error });
  }
  if (putOk.length === 0) return;

  // 5. One confirm request for the whole batch.
  let completeResults: CompleteUploadBatchResult[];
  try {
    completeResults = await api.completeUploadBatch(putOk.map((t) => t.uploadId));
  } catch (err) {
    const msg = getErrorMessage(err);
    for (const t of putOk) push({ fileName: t.item.displayName, status: 'failed', message: msg });
    return;
  }
  const byId = new Map(completeResults.map((r) => [r.uploadId, r]));
  for (const t of putOk) {
    const cr = byId.get(t.uploadId);
    if (cr && cr.status === 'completed') {
      if (t.renamed) push({ fileName: t.item.displayName, status: 'renamed', newName: t.fileName });
      else push({ fileName: t.item.displayName, status: 'uploaded' });
    } else {
      push({ fileName: t.item.displayName, status: 'failed', message: cr?.message || 'Upload verification failed' });
    }
  }
}

/**
 * Upload many files via batched presign + concurrent S3 PUT + batched confirm.
 * `onProgress(settled)` fires as each file resolves so the caller can show a count.
 *
 * Files are grouped by destination folder. When every (folder, name) target is unique
 * (always true for folder uploads, since relative paths are unique), all batches run
 * concurrently. Only when a flat selection contains duplicate names do we keep a folder's
 * batches ordered, so the backend's completed-only collision check renames them
 * deterministically rather than two files racing onto one S3 key.
 */
async function uploadFilesInBatches(
  items: UploadItem[],
  projectId: string,
  onProgress: (settled: number) => void
): Promise<UploadResult[]> {
  const results: UploadResult[] = [];
  let settled = 0;
  const push = (r: UploadResult) => {
    results.push(r);
    settled++;
    onProgress(settled);
  };

  // Does any (folder, name) target repeat? Folder uploads never do; flat selects might.
  // Key on the SANITIZED name — the backend derives s3_key from sanitizeFileName(), so names
  // that differ only in stripped chars (spaces/dots/path-separators/..) collide there and must
  // be seen as duplicates here, or two concurrent batches could race onto one s3_key.
  const seen = new Set<string>();
  let hasDuplicateTargets = false;
  for (const it of items) {
    // NUL separator: stripped by sanitizeFileNameForCollision(), so it cannot appear in either half
    const key = `${it.folderId || ''}${String.fromCharCode(0)}${sanitizeFileNameForCollision(it.file.name)}`;
    if (seen.has(key)) {
      hasDuplicateTargets = true;
      break;
    }
    seen.add(key);
  }

  // Group into per-folder lists, then chunk each into batches.
  const byFolder = new Map<string, UploadItem[]>();
  for (const it of items) {
    const key = it.folderId || '';
    const group = byFolder.get(key);
    if (group) group.push(it);
    else byFolder.set(key, [it]);
  }
  const perFolderBatches: UploadItem[][][] = [];
  for (const group of byFolder.values()) {
    perFolderBatches.push(chunk(group, BATCH_SIZE));
  }

  // A "chain" is a set of batches that must run in order. Unique targets => every batch is
  // its own chain (max parallelism). Duplicate names => each folder's batches form one chain.
  const chains: UploadItem[][][] = hasDuplicateTargets
    ? perFolderBatches
    : perFolderBatches.flat().map((b) => [b]);
  const chainConcurrency = hasDuplicateTargets ? FOLDER_CONCURRENCY : BATCH_CONCURRENCY;

  await mapWithConcurrency(chains, chainConcurrency, async (chain) => {
    for (const batch of chain) {
      await runBatch(batch, projectId, push);
    }
  });

  return results;
}

export function useUpload({ targetFolderId, projectId, onUploadComplete }: UseUploadOptions) {
  const [draggingUpload, setDraggingUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{
    current: number;
    total: number;
    fileName: string;
    phase?: 'creating_folders' | 'uploading_files';
  }>({ current: 0, total: 0, fileName: '' });
  const [uploadResults, setUploadResults] = useState<UploadResult[]>([]);
  // The folder the current results were uploaded into. Results are only surfaced
  // while the user is viewing this folder, so they don't pop back up when the user
  // navigates into a subfolder they just uploaded. Captured at upload time, so it
  // stays correct even if the user navigates away mid-upload.
  const [uploadResultsFolderId, setUploadResultsFolderId] = useState<string | null>(null);
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [emptyFolderPaths, setEmptyFolderPaths] = useState<string[]>([]);
  const [showUploadDropdown, setShowUploadDropdown] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef(0);
  const uploadDropdownRef = useRef<HTMLDivElement>(null);

  // Upload drag handlers
  const handleUploadDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.types.includes('Files')) {
      e.dataTransfer.dropEffect = 'copy';
    }
  };

  const handleUploadDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.types.includes('Files')) {
      dragCounterRef.current++;
      if (dragCounterRef.current === 1) {
        setDraggingUpload(true);
      }
    }
  };

  const handleUploadDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setDraggingUpload(false);
    }
  };

  const addPendingFiles = (filesToAdd: File[]) => {
    const newPendingFiles: PendingFile[] = filesToAdd.map((file) => ({
      file,
      id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
      validationError: getFileValidationError(file),
    }));
    setPendingFiles((prev) => [...prev, ...newPendingFiles]);
    setUploadResults([]);
  };

  const addPendingFilesWithPaths = (collected: CollectedFile[], emptyPaths: string[]) => {
    const newPending: PendingFile[] = collected.map(({ file, relativePath }) => ({
      file,
      id: `${relativePath}-${file.size}-${Date.now()}-${Math.random()}`,
      validationError: getFileValidationError(file),
      relativePath,
    }));
    setPendingFiles((prev) => [...prev, ...newPending]);
    if (emptyPaths.length > 0) {
      setEmptyFolderPaths((prev) => [...prev, ...emptyPaths]);
    }
    setUploadResults([]);
  };

  const handleUploadDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setDraggingUpload(false);

    // Ignore internal file-to-folder or folder-to-folder drags
    if (e.dataTransfer.types.includes('application/x-file-id') ||
        e.dataTransfer.types.includes('application/x-folder-id')) {
      return;
    }

    const items = e.dataTransfer.items;
    const entries: FileSystemEntry[] = [];

    if (items) {
      for (let i = 0; i < items.length; i++) {
        const entry = items[i].webkitGetAsEntry?.();
        if (entry) {
          entries.push(entry);
        }
      }
    }

    const hasDirectories = entries.some((entry) => entry.isDirectory);

    if (hasDirectories) {
      const allFiles: CollectedFile[] = [];
      const allEmptyPaths: string[] = [];
      for (const entry of entries) {
        const result = await collectFilesFromEntry(entry);
        allFiles.push(...result.files);
        allEmptyPaths.push(...result.emptyFolderPaths);
      }
      addPendingFilesWithPaths(allFiles, allEmptyPaths);
    } else {
      const droppedFiles = Array.from(e.dataTransfer.files);
      if (droppedFiles.length > 0) {
        addPendingFiles(droppedFiles);
      }
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length > 0) {
      addPendingFiles(selectedFiles);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFolderInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length > 0) {
      const collected: CollectedFile[] = selectedFiles.map((file) => ({
        file,
        relativePath: (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name,
      }));
      addPendingFilesWithPaths(collected, []);
    }
    if (folderInputRef.current) {
      folderInputRef.current.value = '';
    }
  };

  const removePendingFile = (id: string) => {
    setPendingFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const clearPendingFiles = () => {
    setPendingFiles([]);
    setEmptyFolderPaths([]);
  };

  const processFiles = useCallback(async (filesToProcess: File[]) => {
    setUploading(true);
    setUploadResults([]);

    const items: UploadItem[] = filesToProcess.map((file) => ({
      file,
      folderId: targetFolderId || undefined,
      displayName: file.name,
    }));
    const total = items.length;
    setUploadProgress({ current: 0, total, fileName: '', phase: 'uploading_files' });

    // Throttle progress re-renders — bulk uploads settle thousands of files.
    let lastShown = 0;
    const results = await uploadFilesInBatches(items, projectId, (settled) => {
      if (settled === total || settled - lastShown >= 25) {
        lastShown = settled;
        setUploadProgress({ current: settled, total, fileName: '', phase: 'uploading_files' });
      }
    });

    setUploadResults(results);
    setUploadResultsFolderId(targetFolderId);
    setUploading(false);
    setUploadProgress({ current: 0, total: 0, fileName: '' });

    if (results.some((r) => r.status === 'uploaded' || r.status === 'renamed')) {
      onUploadComplete();
    }
  }, [targetFolderId, projectId, onUploadComplete]);

  const processFolderUpload = useCallback(async (validFiles: PendingFile[]) => {
    setUploading(true);
    setUploadResults([]);
    const results: UploadResult[] = [];

    // Phase 1: Create folders
    const allPaths = [
      ...validFiles.filter((f) => f.relativePath).map((f) => f.relativePath!),
      ...emptyFolderPaths,
    ];
    const folderPaths = extractFolderPaths(allPaths);
    const folderIdMap: Record<string, string> = {};
    const totalSteps = folderPaths.length + validFiles.length;

    for (let i = 0; i < folderPaths.length; i++) {
      const folderPath = folderPaths[i];
      const parts = folderPath.split('/');
      const folderName = parts[parts.length - 1];
      const parentPath = parts.slice(0, -1).join('/');

      let parentId: string | undefined;
      if (parentPath) {
        parentId = folderIdMap[parentPath];
      } else {
        parentId = targetFolderId || undefined;
      }

      setUploadProgress({
        current: i + 1,
        total: totalSteps,
        fileName: folderName,
        phase: 'creating_folders',
      });

      try {
        const folder = await api.createFolder(folderName, projectId, parentId);
        folderIdMap[folderPath] = folder.id;
        results.push({ fileName: folderPath, status: 'folder_created', message: 'Folder created' });
      } catch (err: unknown) {
        const errMsg = getErrorMessage(err);
        if (errMsg.includes('already exists')) {
          try {
            const { folders: siblings } = await api.listFolders(projectId, parentId);
            const existing = siblings.find(
              (f) => f.name.toLowerCase() === folderName.toLowerCase(),
            );
            if (existing) {
              folderIdMap[folderPath] = existing.id;
              results.push({ fileName: folderPath, status: 'folder_skipped', message: 'Folder already exists' });
            } else {
              results.push({ fileName: folderPath, status: 'failed', message: errMsg });
            }
          } catch {
            results.push({ fileName: folderPath, status: 'failed', message: errMsg });
          }
        } else {
          results.push({ fileName: folderPath, status: 'failed', message: errMsg });
        }
      }
    }

    // Phase 2: Upload files (batched + concurrent). Each file is routed to the folder
    // created in Phase 1 (or the target folder for root-level files).
    const fileItems: UploadItem[] = validFiles.map((pending) => {
      let uploadFolderId = targetFolderId || undefined;
      if (pending.relativePath) {
        const pathParts = pending.relativePath.split('/');
        const parentFolderPath = pathParts.slice(0, -1).join('/');
        if (parentFolderPath && folderIdMap[parentFolderPath]) {
          uploadFolderId = folderIdMap[parentFolderPath];
        }
      }
      return {
        file: pending.file,
        folderId: uploadFolderId,
        displayName: pending.relativePath || pending.file.name,
      };
    });

    let lastShown = 0;
    const fileResults = await uploadFilesInBatches(fileItems, projectId, (settled) => {
      if (settled === fileItems.length || settled - lastShown >= 25) {
        lastShown = settled;
        setUploadProgress({
          current: folderPaths.length + settled,
          total: totalSteps,
          fileName: '',
          phase: 'uploading_files',
        });
      }
    });
    results.push(...fileResults);

    setUploadResults(results);
    setUploadResultsFolderId(targetFolderId);
    setUploading(false);
    setUploadProgress({ current: 0, total: 0, fileName: '' });

    if (results.some((r) => r.status === 'uploaded' || r.status === 'renamed' || r.status === 'folder_created')) {
      onUploadComplete();
    }
  }, [targetFolderId, projectId, emptyFolderPaths, onUploadComplete]);

  const uploadPendingFiles = async () => {
    const validFiles = pendingFiles.filter((f) => !f.validationError);
    if (validFiles.length === 0) return;

    // Never start an upload on a dead session: try a silent refresh first, and if the
    // session still isn't valid, send the user to sign-in WITHOUT issuing any S3 PUTs.
    const sessionValid = await api.ensureValidToken();
    if (!sessionValid) {
      api.expireSession();
      return;
    }

    const hasFolderUploads = validFiles.some((f) => f.relativePath);

    // Mark a busy operation for the whole upload so a deploy auto-refresh or an expired-token
    // redirect is deferred until it finishes (see busyTracker/reloadGate). begin/end wrap the
    // awaited work in try/finally — not a useEffect — so unmounting FileBrowser mid-upload
    // can't strand the counter.
    busyTracker.begin();
    try {
      if (hasFolderUploads) {
        await processFolderUpload(validFiles);
      } else {
        await processFiles(validFiles.map((f) => f.file));
      }
    } finally {
      busyTracker.end();
    }
    setPendingFiles([]);
    setEmptyFolderPaths([]);
  };

  const clearUploadResults = () => {
    setUploadResults([]);
    setUploadResultsFolderId(null);
  };

  return {
    // State
    draggingUpload,
    uploading,
    uploadProgress,
    uploadResults,
    // Results are tied to the folder they were uploaded into: hidden once the user
    // navigates elsewhere, shown again if they return (until dismissed).
    uploadResultsVisible: uploadResults.length > 0 && uploadResultsFolderId === targetFolderId,
    pendingFiles,
    showUploadDropdown,
    setShowUploadDropdown,

    // Refs
    fileInputRef,
    folderInputRef,
    uploadDropdownRef,

    // Drag handlers
    handleUploadDragOver,
    handleUploadDragEnter,
    handleUploadDragLeave,
    handleUploadDrop,

    // Input handlers
    handleFileInputChange,
    handleFolderInputChange,

    // Pending file management
    removePendingFile,
    clearPendingFiles,
    uploadPendingFiles,

    // Results
    clearUploadResults,
  };
}
